import React from 'react';
import { createAction } from 'redux-actions';
import { actionPrefix } from './constants';
import { ModalType, ModalOptions } from './types';
import { Props as ConfirmationModalProps } from '../../Popup/ConfirmationModal';
import { pushModal } from './basicActions';
import { PapiDeviation, PapiComment, PapiUser } from '@wix/da-papi-types';
import { verifyLoggedOutAccess } from '../../redux/loggedout/actionCreators';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { NotificationType } from '../../Notifications';

export {
  pushModal,
  popModal,
  replaceModal,
  removeModal,
  removeModalsOfType,
} from './basicActions';

export const pushBadgeModal = verifyLoggedOutAccess(
  ReferralType.GIVE,
  (
    username: string,
    onBadgeSent?: () => void,
    onClose?: () => void,
    badgeType: string = 'llama'
  ) =>
    pushModal(ModalType.GIVE_BADGE, {
      params: {
        username,
        onBadgeSent,
        onClose,
        badgeType,
      },
    })
);

export const pushLegacyLlamaModal = verifyLoggedOutAccess(
  ReferralType.GIVE,
  (
    username: string,
    onBadgeSent?: () => void,
    onClose?: () => void,
    badgeType: string = 'llama'
  ) =>
    pushModal(ModalType.LEGACY_GIVE_LLAMA_BADGE, {
      params: {
        username,
        onBadgeSent,
        onClose,
        badgeType,
      },
    })
);

export const pushCommentsModal = (
  typeId: number,
  itemId: number,
  commentId: number,
  parentComment: PapiComment
) =>
  pushModal(ModalType.COMMENT_THREAD, {
    params: { commentId, typeId, itemId, parentComment },
  });

export const pushConfirmationModal = (params: ConfirmationModalProps) =>
  pushModal(ModalType.CONFIRMATION, {
    params,
    wrapInModal: false,
  });

export interface GlobalConfirmationPopupProps
  extends Omit<
    ConfirmationModalProps,
    'title' | 'text' | 'withCancelButton' | 'cancelBtnLabel' | 'confirmBtnLabel'
  > {
  titleTransKey: string;
  textTransKey?: string | React.ReactNode;
  textComponent?: React.ComponentType;
  withCancelButton?: boolean;
  confirmBtnLabelTransKey: string;
  cancelBtnLabelTransKey?: string;
  translationOptions?: object;
}

export const pushGlobalConfirmationModal = (
  params: GlobalConfirmationPopupProps
) =>
  pushModal(ModalType.GLOBAL_CONFIRMATION, {
    params,
    wrapInModal: false,
  });

export const pushGlobalConfirmationModalForMM = (
  params: GlobalConfirmationPopupProps
) =>
  pushModal(ModalType.GLOBAL_CONFIRMATION_FOR_MM, {
    params,
    wrapInModal: false,
  });

export const confirmValue = createAction(
  `${actionPrefix}.CONFIRM_VALUE`,
  payload => payload
);
export const confirmYes = createAction(
  `${actionPrefix}.CONFIRM_YES`,
  () => ({})
);
export const confirmNo = createAction(`${actionPrefix}.CONFIRM_NO`, () => ({}));

export const doNotShowAgainChanged = createAction(
  `${actionPrefix}.DO_NOT_SHOW_CHANGED`,
  (key, value) => ({ key, value })
);

export const showSubmitModal = verifyLoggedOutAccess(
  ReferralType.SUBMIT,
  (type: ModalType, options?: ModalOptions) =>
    pushModal(type, { wrapInModal: false, ...(options || {}) })
);

export const showFavouritedByModal = (deviation: PapiDeviation) =>
  pushModal(ModalType.FAVOURITED_BY, {
    wrapInModal: false,
    params: {
      deviationId: deviation.deviationId,
      typeId: deviation.typeId,
    },
  });

export const showDeviantsListModal = (
  selectedUserId?: number,
  onItemClick?: (user: PapiUser) => void
) =>
  pushModal(ModalType.DEVIANTS_LIST, {
    wrapInModal: false,
    params: {
      selectedUserId,
      onItemClick,
    },
  });

export const showDiscordConnectSuccessModal = (guildId: string | undefined) =>
  pushModal(ModalType.DISCORD_CONNECT_SUCCESS, { params: { guildId } });

export const showDiscordDisconnectModal = () =>
  pushModal(ModalType.DISCORD_DISCONNECT, {});

export const showBadgeInfoModal = (
  username: string,
  badgeId?: number,
  typeId?: number,
  isUnearned?: boolean
) =>
  pushModal(ModalType.BADGE_INFO, {
    params: { username, badgeId, typeId, isUnearned },
    wrapInModal: false,
  });

export const pushAddToGroupModal = (deviation: PapiDeviation, biData: any) =>
  pushModal(ModalType.ADD_TO_GROUP, {
    params: { deviation, biData },
    wrapInModal: false,
  });

export const pushPrivateCollectionModal = (
  collectibleDeviation: PapiDeviation,
  notificationQueue: string | NotificationType,
  ignorePreselectedCollections?: boolean
) =>
  pushModal(ModalType.COLLECTION_POPOVER, {
    params: {
      collectibleDeviation,
      notificationQueue,
      isPrivateCollections: true,
      ignorePreselectedCollections,
    },
  });

export const pushAdoptsBuyerModal = (
  deviation: PapiDeviation,
  initialScreen?: string
) =>
  pushModal(ModalType.ADOPT_BUYER_MODAL, {
    wrapInModal: false,
    params: {
      deviation,
      initialScreen,
    },
  });

export const pushAdoptsSellerModal = (
  deviations: PapiDeviation[],
  initialScreen?: string,
  extraScreenProps?: any
) =>
  pushModal(ModalType.ADOPT_SELLER_MODAL, {
    wrapInModal: false,
    params: {
      deviations,
      initialScreen,
      extraScreenProps,
    },
  });

export const pushAdoptsSellerGiftModal = (deviation: PapiDeviation) =>
  pushModal(ModalType.ADOPT_SELLER_GIFT_MODAL, {
    wrapInModal: false,
    params: {
      deviation,
    },
  });

export const pushAdoptsOrderOriginalDeviationModal = (
  deviation: PapiDeviation
) =>
  pushModal(ModalType.ADOPT_ORDER_ORIGINAL_DEVIATION_MODAL, {
    wrapInModal: false,
    params: {
      deviation,
    },
  });

export const pushExclusiveTransactionHistoryModal = (
  deviation: PapiDeviation
) =>
  pushModal(ModalType.EXCLUSIVE_TRANSACTION_HISTORY_MODAL, {
    wrapInModal: false,
    params: {
      deviation,
    },
  });

export const pushUserSettingsModal = () =>
  pushModal(ModalType.CONTENT_FILTER_SETTINGS, {
    wrapInModal: false,
    params: {},
  });

export const pushBoostModal = (
  deviation: PapiDeviation,
  sectionNameOverride?: string,
  onRequestClose?: () => void,
  onSuccess?: () => void
) =>
  pushModal(ModalType.BOOST_DEVIATION, {
    wrapInModal: false,
    params: {
      deviation,
      sectionNameOverride,
      onRequestClose,
      onSuccess,
    },
  });
